import * as RTabs from "@radix-ui/react-tabs";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import TrackCard from "@/components/ui/TrackCard";
import { useState } from "react";
import { Box, Button, Flex, Grid, Heading } from "@radix-ui/themes";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { LuFilter } from "react-icons/lu";

import { useTracksInfiniteList } from "@/lib/lutely-api/hooks";
import { marketplaceTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { PillTabsList } from "@/components/ui/PillTabsList";
import CreatorsSection from "./CreatorsSection";
import FeaturedAlbumsSection from "./FeaturedAlbumsSection";
import FilterBar from "./FilterBar";
import LegacyFilterSection from "./FilterSection/LegacyFilterSection";
import { getKeyQueryPitches } from "./FilterSection/shared/keySignatures";
import TrackSliderSection from "./TrackSliderSection";
import MobileFiltersDrawer from "./mobile-filters/MobileFiltersDrawer";
import { TracksControllerTracksListSortBy } from "@/lib/lutely-api/gen/dtos/tracksControllerTracksListSortBy";
import { analytics, EventEnum } from "@/lib/analytics";

const titleMap: Record<string, string> = {
  songs: "Songs",
  beats: "Beats",
  soundkits: "Sound Kits",
};

const MARKETPLACE_TABS = [
  { value: "all", label: "All" },
  { value: "songs", label: "Songs" },
  { value: "beats", label: "Beats" },
  { value: "soundkits", label: "Sounds" },
] as const;

type MarketplaceTabValue = (typeof MARKETPLACE_TABS)[number]["value"];

const MARKETPLACE_TAB_VALUES = new Set<MarketplaceTabValue>(
  MARKETPLACE_TABS.map((tab) => tab.value),
);

function isMarketplaceTabValue(
  value: string | undefined,
): value is MarketplaceTabValue {
  if (!value) {
    return false;
  }
  return MARKETPLACE_TAB_VALUES.has(value as MarketplaceTabValue);
}

function unpackCurrentQuery({
  songsQuery,
  beatsQuery,
  soundkitsQuery,
  trackTypeFilter,
}: {
  songsQuery: ReturnType<typeof useTracksInfiniteList>;
  beatsQuery: ReturnType<typeof useTracksInfiniteList>;
  soundkitsQuery: ReturnType<typeof useTracksInfiniteList>;
  trackTypeFilter: string | undefined;
}) {
  if (
    trackTypeFilter === "songs" ||
    !trackTypeFilter ||
    trackTypeFilter === "all"
  ) {
    return {
      tracks: songsQuery.data?.pages.flatMap((page) => page.content) || [],
      fetchNextPage: songsQuery.fetchNextPage,
      hasNextPage: songsQuery.hasNextPage,
      isFetchingNextPage: songsQuery.isFetchingNextPage,
    };
  } else if (trackTypeFilter === "beats") {
    return {
      tracks: beatsQuery.data?.pages.flatMap((page) => page.content) || [],
      fetchNextPage: beatsQuery.fetchNextPage,
      hasNextPage: beatsQuery.hasNextPage,
      isFetchingNextPage: beatsQuery.isFetchingNextPage,
    };
  } else {
    return {
      tracks: soundkitsQuery.data?.pages.flatMap((page) => page.content) || [],
      fetchNextPage: soundkitsQuery.fetchNextPage,
      hasNextPage: soundkitsQuery.hasNextPage,
      isFetchingNextPage: soundkitsQuery.isFetchingNextPage,
    };
  }
}

const trackTypeToKind: Record<string, "SONG" | "BEAT" | "SOUNDKIT"> = {
  songs: "SONG",
  beats: "BEAT",
  soundkits: "SOUNDKIT",
};

export default function LegacyMarketplace() {
  const searchParameters = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const navigate = useNavigate();
  const isDesktopLayout = useMediaQuery("(min-width: 1024px)");
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const onSetFilter = (updates: any) => {
    analytics.track(EventEnum.MARKETPLACE_FILTER_SELECTED, {
      updatedFilters: updates,
    });
    navigate({
      to: "/marketplace",
      search: (old) => {
        delete old.query;
        return {
          ...old,
          ...updates,
        };
      },
    });
  };

  const onRemoveFilter = (param: string) => {
    navigate({
      to: "/marketplace",
      search: (old) => {
        return { ...old, [param]: undefined };
      },
    });
  };

  const trackTypeFilter = searchParameters["type"];
  const typeTabsValue: MarketplaceTabValue = isMarketplaceTabValue(
    trackTypeFilter,
  )
    ? trackTypeFilter
    : "all";
  const isAllTracks = typeTabsValue === "all";

  const handleTypeTabChange = (nextValue: string) => {
    if (nextValue === typeTabsValue) {
      return;
    }
    onSetFilter({ type: nextValue });
  };

  const hideSliderArrows = false;

  const handleFiltersClick = () => {
    setIsMobileFiltersOpen(true);
  };

  const {
    bpm: bpmFilter,
    genre: genreFilter,
    mood: moodFilter,
    key: keyFilter,
    query: queryFilter,
  } = searchParameters;
  let bpmStart, bpmEnd, genres, moods, keySignatures, search;

  if (bpmFilter) {
    const bpmParts = bpmFilter.split("-");
    if (bpmParts.length === 2) {
      bpmStart = parseInt(bpmParts[0]);
      bpmEnd = parseInt(bpmParts[1]);
    }
  }

  if (genreFilter && genreFilter.length > 0) {
    genres = genreFilter.split(", ");
  }

  if (moodFilter && moodFilter.length > 0) {
    moods = moodFilter.split(", ");
  }

  if (keyFilter && keyFilter.length > 0) {
    keySignatures = getKeyQueryPitches(keyFilter) ?? [keyFilter];
  }

  if (queryFilter && queryFilter.length > 0) {
    search = queryFilter;
  }

  const songAndBeatQueryParams: {
    bpmStart?: number;
    bpmEnd?: number;
    genres?: string[];
    moods?: string[];
    keySignatures?: string[];
    search?: string;
    sortBy?: TracksControllerTracksListSortBy;
  } = {
    bpmStart,
    bpmEnd,
    genres,
    moods,
    keySignatures,
    search,
    sortBy: undefined,
  };

  const soundkitQueryParams: {
    genres?: string[];
    search?: string;
    sortBy?: TracksControllerTracksListSortBy;
  } = {
    search,
    genres,
    sortBy: undefined,
  };

  if (Object.values(songAndBeatQueryParams).every((v) => v === undefined)) {
    songAndBeatQueryParams.sortBy = "random";
  }

  if (Object.values(soundkitQueryParams).every((v) => v === undefined)) {
    soundkitQueryParams.sortBy = "random";
  }

  const songsQuery = useTracksInfiniteList(
    {
      type: "SONG",
      pageSize: 20,
      published: true,
      ...songAndBeatQueryParams,
    },
    { enabled: isAllTracks || typeTabsValue === "songs" },
  );
  const beatsQuery = useTracksInfiniteList(
    {
      type: "BEAT",
      pageSize: 20,
      published: true,
      ...songAndBeatQueryParams,
    },
    { enabled: isAllTracks || typeTabsValue === "beats" },
  );
  const soundkitsQuery = useTracksInfiniteList(
    {
      type: "SOUNDKIT",
      pageSize: 20,
      published: true,
      ...soundkitQueryParams,
    },
    { enabled: isAllTracks || typeTabsValue === "soundkits" },
  );

  const { tracks, fetchNextPage, hasNextPage, isFetchingNextPage } =
    unpackCurrentQuery({
      songsQuery,
      beatsQuery,
      soundkitsQuery,
      trackTypeFilter: typeTabsValue,
    });

  const creatorsKind = trackTypeToKind[typeTabsValue] ?? "SONG";

  return (
    <ContainerWrapper>
      <Flex
        direction={isDesktopLayout ? "row" : "column"}
        className={cn(
          "w-full self-center justify-self-center",
          isDesktopLayout
            ? "min-h-screen items-start pt-[25px] pb-[150px]"
            : "min-h-screen",
        )}
      >
        {isDesktopLayout && (
          <LegacyFilterSection
            searchParameters={searchParameters}
            onSetFilter={onSetFilter}
            onRemoveFilter={onRemoveFilter}
          />
        )}

        <Flex
          className={cn(
            "flex-1 min-w-0",
            isDesktopLayout && "w-full h-full flex-col",
          )}
          direction="column"
        >
          {isDesktopLayout ? (
            <FilterBar
              searchParameters={searchParameters as any}
              onRemoveFilter={onRemoveFilter as any}
            />
          ) : (
            <Flex className="w-full flex-col gap-[12px] pt-[16px] px-4 shrink-0">
              <RTabs.Root
                value={typeTabsValue}
                onValueChange={handleTypeTabChange}
                className="w-full"
              >
                <Flex className="w-full items-center gap-2">
                  <div className="flex-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <PillTabsList tabs={MARKETPLACE_TABS} />
                  </div>
                  <Button
                    onClick={handleFiltersClick}
                    color="gray"
                    radius="full"
                  >
                    <LuFilter className="text-[16px]" />
                    Filters
                  </Button>
                </Flex>
              </RTabs.Root>

              <Flex className="-mx-4 overflow-x-auto pb-1 px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <FilterBar
                  searchParameters={searchParameters as any}
                  onRemoveFilter={onRemoveFilter as any}
                  className="mb-0 min-w-max flex-nowrap"
                />
              </Flex>
            </Flex>
          )}

          <Box className={cn(isDesktopLayout ? "contents" : "px-4 pb-[150px]")}>
            <div
              className={cn(isDesktopLayout ? "" : "flex flex-col gap-[24px]")}
            >
              <FeaturedAlbumsSection variant="stacked" />

              <CreatorsSection
                hideArrows={hideSliderArrows}
                kind={creatorsKind}
                searchParameters={searchParameters as any}
              />

              {isAllTracks ? (
                <>
                  <TrackSliderSection
                    title={titleMap["songs"]}
                    trackType="songs"
                    query={songsQuery}
                    hideArrows={hideSliderArrows}
                  />
                  <TrackSliderSection
                    title={titleMap["beats"]}
                    trackType="beats"
                    query={beatsQuery}
                    hideArrows={hideSliderArrows}
                  />
                  <TrackSliderSection
                    title={titleMap["soundkits"]}
                    trackType="soundkits"
                    query={soundkitsQuery}
                    hideArrows={hideSliderArrows}
                  />
                </>
              ) : (
                <Flex direction="column" className="pt-[40px]">
                  <Heading as="h2" className={marketplaceTokens.sectionHeader}>
                    {titleMap[typeTabsValue]}
                  </Heading>
                  {isDesktopLayout ? (
                    <Flex wrap="wrap" className="mt-[20px]" gap="30px">
                      {tracks.map((track) => (
                        <TrackCard key={track.trackId} track={track} />
                      ))}
                    </Flex>
                  ) : (
                    <Grid
                      className="mt-[20px]"
                      columns={{ initial: "2" }}
                      gap="4"
                    >
                      {tracks.map((track) => (
                        <TrackCard
                          key={track.trackId}
                          track={track}
                          className="w-full"
                        />
                      ))}
                    </Grid>
                  )}
                  <Flex className="justify-center mt-8">
                    <Button
                      onClick={() => fetchNextPage()}
                      disabled={!hasNextPage || isFetchingNextPage}
                      loading={isFetchingNextPage}
                      variant="soft"
                      size="2"
                      className="cursor-pointer"
                    >
                      {isFetchingNextPage
                        ? "Loading..."
                        : hasNextPage
                          ? "Load More"
                          : "End of list"}
                    </Button>
                  </Flex>
                </Flex>
              )}
            </div>
          </Box>
        </Flex>
        {!isDesktopLayout && (
          <MobileFiltersDrawer
            open={isMobileFiltersOpen}
            onOpenChange={setIsMobileFiltersOpen}
            searchParameters={searchParameters as any}
            onSetFilter={onSetFilter}
            onRemoveFilter={onRemoveFilter as any}
          />
        )}
      </Flex>
    </ContainerWrapper>
  );
}
